import { LitElement, html, css } from 'lit';
import { Router } from '@vaadin/router';
import { HTTP_CLIENT, setupAxios } from './middleware/axiosClient';
import './landing_page/components/header.js';
import './landing_page/components/footer.js';
import './header-bar-element.js';
import './whats-new-modal.js';
import logo from './images/logo.webp';
import './shared/components/ticker-search-sidebar-element.js';
import './shared/components/loading_indicators/spinner-element.js';
import './shared/components/cookie-consent-pill-element.js';

class AppElement extends LitElement {
  static get properties() {
    return {
      collapsedSubNav: { type: Array },
      currentPath: { type: String },
      currentVersion: { type: String },
      navItems: Array,
      pageTitle: String,
      releaseDate: String,
      router: Object,
      tabTitle: String,
      componentsPreloaded: { type: Boolean, state: true },
      hasData: { type: Boolean, state: true },
    };
  }

  constructor() {
    super();
    this.collapsedSubNav = [false, true];
    this.currentPath = '';
    this.pageTitle = '';
    this.navItems = [];
    this.isActive = false;
    this.componentsPreloaded = false;
    // Demo state has three sources of truth that must stay in sync:
    //   1. this.hasData           — LitElement reactive property driving the demo banner
    //   2. localStorage.finovva_has_data — persisted across page reloads (initialises #1)
    //   3. window.__FINOVVA_DEMO__ — read by the axios interceptor and inline guards
    // exitDemoMode() updates all three; checkAuthentication() is the authoritative re-sync on navigation.
    this.hasData = localStorage.getItem('finovva_has_data') === 'true';
    this._onDemoModeExited = null;
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('tabTitle')) {
      document.title = this.tabTitle;
    }
  }

  updated(changedProps) {
    if (changedProps.has('currentPath') && this.currentPath !== '' && !this.isLandingPath(this.currentPath) && !this.isLoginPath(this.currentPath)) {
      this.checkAuthentication();
      this.checkCookieConsent();
      this.setupNavigation();
      if (!this.isInDemoMode) {
        this.refreshStaleTickerData();
      }
    }
    // Clear demo mode when leaving the app (landing/login pages)
    if (changedProps.has('currentPath') && this.currentPath && !this.isProductPath(this.currentPath) && typeof window !== 'undefined') {
      window.__FINOVVA_DEMO__ = false;
      window.__FINOVVA_DEMO_DATA__ = null;
    }
  }


  setupNavigation() {
    const navSidebar = this.shadowRoot.querySelector('.nav-sidebar');
    if (navSidebar) {
      this.navItems = navSidebar.querySelectorAll('li a');
      this.highlightNavItem(this.currentPath);
    }

    const navExpands = this.shadowRoot.querySelectorAll('.nav-link.expandable');
    const subNavs = this.shadowRoot.querySelectorAll('.sub-nav');

    navExpands.forEach((navExpand, index) => {
      const subNav = subNavs[index];

      if (index === 0 && !this.collapsedSubNav[0]) {
        // Expand the first nav item by default
        subNav.classList.add('visible');
        navExpand.classList.add('expanded');
        this.collapsedSubNav[index] = false; // Mark as expanded
      }
      
      if (index === 1 && this.collapsedSubNav[1]) {
        // Collapse other nav items
        subNav.classList.remove('visible');
        navExpand.classList.remove('expanded');
        this.collapsedSubNav[index] = true; // Mark as collapsed
      }

      // Only add the event listener if it hasn't been added already
      if (!navExpand.clickHandlerAdded) {
        navExpand.addEventListener('click', () => {
          subNav.classList.toggle('visible');
          navExpand.classList.toggle('expanded');
          this.collapsedSubNav[index] = !this.collapsedSubNav[index];
        });
        navExpand.clickHandlerAdded = true;
      }
    });
  }

  async firstUpdated() {
    this.checkCookieConsent();
    const router = new Router(this.shadowRoot.querySelector('.view'));

    router.setRoutes([
      {
        path: '/',
        redirect: '/landing',
        action: () => {
          this.tabTitle = 'Home';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/landing',
        component: 'landing-page',
        action: async () => {
          await import('./landing_page/pages/landing.js');
          this.tabTitle = 'Home';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/feature',
        component: 'feature-page',
        action: async () => {
          await import('./landing_page/pages/features.js');
          this.tabTitle = 'Features';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/signup',
        component: 'signup-page',
        action: async () => {
          await import('./landing_page/pages/signup.js');
          this.tabTitle = 'Signup';
          this.currentPath = '/signup';
        },
      },
      {
        path: '/signin',
        component: 'signin-page',
        action: async () => {
          await import('./landing_page/pages/signin.js');
          this.tabTitle = 'Signin';
          this.currentPath = '/signin';
        },
      },
      {
        path: '/forgot-password',
        component: 'forgot-password-page',
        action: async () => {
          await import('./landing_page/pages/forgot-password.js');
          this.tabTitle = 'Forgot Password';
          this.currentPath = '/forgot-password';
        },
      },
      {
        path: '/change-password',
        component: 'change-password-page',
        action: async () => {
          await import('./landing_page/pages/change-password.js');
          this.tabTitle = 'Change Password';
          this.currentPath = '/change-password';
        },
      },
      {
        path: '/change-password-existing',
        component: 'change-with-existing-password-element',
        action: async () => {
          await import('./settings/change-with-existing-password-element.js');
          this.tabTitle = 'Change Existing Password';
          this.currentPath = '/change-password-existing';
        },
      },
      {
        path: '/verification',
        component: 'verification-page',
        action: async () => {
          await import('./landing_page/pages/verification.js');
          this.tabTitle = 'Verification';
          this.currentPath = '/verification';
        },
      },
      {
        path: '/subscribe',
        component: 'subscribe-page',
        action: async () => {
          await import('./landing_page/pages/subscribe.js');
          this.currentPath = '/subscribe';
        },
      },
      {
        path: '/feature-dashboard',
        component: 'dashboard-feature-page',
        action: async () => {
          await import('./landing_page/pages/dashboard-feature-page.js');
          this.tabTitle = 'Features::Dashboard';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/feature-trades',
        component: 'trades-feature-page',
        action: async () => {
          await import('./landing_page/pages/trades-feature-page.js');
          this.tabTitle = 'Feature::Trades';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/feature-performance',
        component: 'performance-feature-page',
        action: async () => {
          await import('./landing_page/pages/performance-feature-page.js');
          this.tabTitle = 'Feature::Performance';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/feature-dividends',
        component: 'dividends-feature-page',
        action: async () => {
          await import('./landing_page/pages/dividends-feature-page.js');
          this.tabTitle = 'Feature::Dividends';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/feature-news',
        component: 'news-feature-page',
        action: async () => {
          await import('./landing_page/pages/news-feature-page.js');
          this.tabTitle = 'Feature::News';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/pricing',
        component: 'pricing-page',
        action: async () => {
          await import('./landing_page/pages/pricing.js');
          this.tabTitle = 'Pricing';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/blog',
        component: 'blog-page',
        action: async () => {
          await import('./landing_page/pages/blog.js');
          this.tabTitle = 'Blog';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/blog/:id',
        component: 'blog-content-page',
        action: async () => {
          await import('./landing_page/pages/blog-content-page.js');
          this.tabTitle = 'Blog Details';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/privacy',
        component: 'privacy-policy-page',
        action: async () => {
          await import('./landing_page/pages/privacy-policy-page.js');
          this.tabTitle = 'Privacy';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/terms',
        component: 'terms-policy-page',
        action: async () => {
          await import('./landing_page/pages/terms-policy-page.js');
          this.tabTitle = 'Terms';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/contact',
        component: 'contact-page',
        action: async () => {
          await import('./landing_page/pages/contact.js');
          this.tabTitle = 'Contact Us';
          this.currentPath = '/landing';
        },
      },
      {
        path: '/demo/:path*',
        redirect: '/landing',
      },
      {
        path: '/app/brokerages',
        component: 'brokerage-list-element',
        action: async () => {
          await import('./brokerages/brokerage-list-element.js');
          this.pageTitle = 'Brokerage List';
          this.tabTitle = 'Brokerage List';
          this.currentPath = '/app/brokerages';
        },
      },
      {
        path: '/app/brokerages/:brokerageSlug',
        component: 'brokerage-connection-element',
        action: async () => {
          await import('./brokerages/brokerage-connection-element.js');
          this.pageTitle = 'Brokerage Connection';
          this.tabTitle = 'Brokerage Connection';
          this.currentPath = '/app/brokerages';
        },
      },
      {
        path: '/app/brokerages/:name/steps',
        component: 'brokerage-element',
        action: async () => {
          await import('./brokerages/brokerage-element.js');
          this.pageTitle = 'Brokerage';
          this.tabTitle = 'Brokerage';
          this.currentPath = '/app/brokerages';
        },
      },
      {
        path: '/app/brokerages/:name/review/:reviewSessionId',
        component: 'import-review-element',
        action: async () => {
          await import('./brokerages/import-review-element.js');
          this.pageTitle = 'Import Review';
          this.tabTitle = 'Import Review';
          this.currentPath = '/app/brokerages';
        },
      },
      {
        path: '/app/dashboard',
        component: 'dashboard-element',
        action: () => {
          this.highlightNavItem('/app/dashboard');
          this.pageTitle = 'Dashboard';
          this.tabTitle = 'Dashboard';
          this.currentPath = '/app/dashboard';
        },
      },
      {
        path: '/app/analytics',
        children: [
          {
            path: 'performance',
            component: 'performance-element',
            action: () => {
              this.highlightNavItem('/app/analytics/performance');
              this.pageTitle = 'Performance Analysis';
              this.tabTitle = 'Performance';
              this.currentPath = '/app/analytics/performance';
            },
          },
          {
            path: 'dividends',
            component: 'dividend-element',
            action: () => {
              this.highlightNavItem('/app/analytics/dividends');
              this.pageTitle = 'Dividend Analysis';
              this.tabTitle = 'Dividends';
              this.currentPath = '/app/analytics/dividends';
            },
          },
          {
            path: 'risk',
            component: 'risk-element',
            action: async () => {
              await import('./analytics/risk/risk-element.js');
              this.highlightNavItem('/app/analytics/risk');
              this.pageTitle = 'Risk Analysis';
              this.tabTitle = 'Risk';
              this.currentPath = '/app/analytics/risk';
            },
          },
        ],
      },
      {
        path: '/app/holdings',
        component: 'holdings-element',
        action: () => {
          this.highlightNavItem('/app/holdings');
          this.pageTitle = 'Holdings';
          this.tabTitle = 'Holdings';
          this.currentPath = '/app/holdings';
        },
      },
      {
        path: '/app/trades',
        action: () => {
          Router.go('/app/transactions');
        },
      },
      {
        path: '/app/transactions',
        component: 'transactions-element',
        action: () => {
          this.highlightNavItem('/app/transactions');
          this.pageTitle = 'Transactions';
          this.tabTitle = 'Transactions';
          this.currentPath = '/app/transactions';
        },
      },
      {
        path: '/app/transactions/sync',
        component: 'trade-sync-element',
        action: async () => {
          await import('./trades/trade-sync-element.js');
          this.highlightNavItem('/app/transactions');
          this.pageTitle = 'Transactions';
          this.tabTitle = 'Syncing Transactions';
          this.currentPath = '/app/transactions';
        },
      },
      {
        path: '/app/news',
        component: 'news-wrapper-element',
        action: () => {
          this.highlightNavItem('/app/news');
          this.pageTitle = 'News';
          this.tabTitle = 'News';
          this.currentPath = '/app/news';
        },
      },
      {
        path: '/app/settings',
        component: 'settings-element',
        action: () => {
          this.highlightNavItem('/app/settings');
          this.pageTitle = 'Settings';
          this.tabTitle = 'Settings';
          this.currentPath = '/app/settings';
        },
      },
      {
        path: '/app/analytics/:ticker',
        redirect: '/app/analytics/:ticker/summary',
      },
      {
        path: '/app/analytics/:ticker/summary',
        component: 'symbol-information-element',
        action: async (context) => {
          await Promise.all([
            import('./analytics/symbol/symbol-information-element.js'),
            import('./analytics/symbol/summary-cards/symbol-summary-element.js'),
            import('./analytics/symbol/trades/symbol-trades-element.js'),
            import('./analytics/symbol/financials/symbol-financials-element.js'),
            import('./analytics/symbol/dividends/symbol-dividends-element.js')
          ]);
          const ticker = context.params.ticker;
          this.pageTitle = `${ticker.toUpperCase()} - Summary`;
          this.tabTitle = `${ticker.toUpperCase()} - Summary`;
          this.currentPath = `/app/analytics/${ticker}/summary`;
        },
      },
      {
        path: '/app/analytics/:ticker/trades',
        component: 'symbol-information-element',
        action: async (context) => {
          await Promise.all([
            import('./analytics/symbol/symbol-information-element.js'),
            import('./analytics/symbol/summary-cards/symbol-summary-element.js'),
            import('./analytics/symbol/trades/symbol-trades-element.js'),
            import('./analytics/symbol/financials/symbol-financials-element.js'),
            import('./analytics/symbol/dividends/symbol-dividends-element.js')
          ]);
          const ticker = context.params.ticker;
          this.pageTitle = `${ticker.toUpperCase()} - Trades`;
          this.tabTitle = `${ticker.toUpperCase()} - Trades`;
          this.currentPath = `/app/analytics/${ticker}/trades`;
        },
      },
      {
        path: '/app/analytics/:ticker/financials',
        component: 'symbol-information-element',
        action: async (context) => {
          await Promise.all([
            import('./analytics/symbol/symbol-information-element.js'),
            import('./analytics/symbol/summary-cards/symbol-summary-element.js'),
            import('./analytics/symbol/trades/symbol-trades-element.js'),
            import('./analytics/symbol/financials/symbol-financials-element.js'),
            import('./analytics/symbol/dividends/symbol-dividends-element.js')
          ]);
          const ticker = context.params.ticker;
          this.pageTitle = `${ticker.toUpperCase()} - Financials`;
          this.tabTitle = `${ticker.toUpperCase()} - Financials`;
          this.currentPath = `/app/analytics/${ticker}/financials`;
        },
      },
      {
        path: '/app/analytics/:ticker/dividends',
        component: 'symbol-information-element',
        action: async (context) => {
          await Promise.all([
            import('./analytics/symbol/symbol-information-element.js'),
            import('./analytics/symbol/summary-cards/symbol-summary-element.js'),
            import('./analytics/symbol/trades/symbol-trades-element.js'),
            import('./analytics/symbol/financials/symbol-financials-element.js'),
            import('./analytics/symbol/dividends/symbol-dividends-element.js')
          ]);
          const ticker = context.params.ticker;
          this.pageTitle = `${ticker.toUpperCase()} - Dividends`;
          this.tabTitle = `${ticker.toUpperCase()} - Dividends`;
          this.currentPath = `/app/analytics/${ticker}/dividends`;
        },
      },
    ]);

    // // This is updated manually
    // this.currentVersion = '0.1.0.beta.1';
    // this.releaseDate = 'October 26, 2023';
  
    // const storedVersion = localStorage.getItem('appVersion');
  
    // // Show the user what's new if a new version has been issued
    // if (storedVersion !== this.currentVersion) {
    //   const whatsNewModal = this.shadowRoot.querySelector('whats-new-modal');
    //   setTimeout(() => {
    //     const modalElement = whatsNewModal.shadowRoot.querySelector('modal-element');
    //     modalElement.setAttribute('show', '');
    //   }, 1);
  
    //   localStorage.setItem('appVersion', this.currentVersion);
    // }

    // Re-sync hasData in case exitDemoMode() fired between constructor and firstUpdated()
    const storedHasData = localStorage.getItem('finovva_has_data') === 'true';
    if (storedHasData !== this.hasData) {
      this.hasData = storedHasData;
    }

    this._onDemoModeExited = () => { this.hasData = true; };
    window.addEventListener('demo-mode-exited', this._onDemoModeExited);

    setupAxios();

    setTimeout(() => {
      this.preloadMainNavComponents();
    }, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._onDemoModeExited) {
      window.removeEventListener('demo-mode-exited', this._onDemoModeExited);
      this._onDemoModeExited = null;
    }
  }

  async preloadMainNavComponents() {
    try {
      await Promise.all([
        import('./dashboard/dashboard-element.js'),
        import('./analytics/performance/performance-element.js'),
        import('./analytics/dividends/dividend-element.js'),
        import('./holdings/holdings-element.js'),
        import('./trades/transactions-element.js'),
        import('./news/news-wrapper-element.js'),
        import('./settings/settings-element.js')
      ]);
      this.componentsPreloaded = true;
    } catch (error) {
      console.debug('Main nav components preload completed with some errors:', error);
      this.componentsPreloaded = true;
    }
  }


  toggleState() {
    this.isActive = !this.isActive;
    this.requestUpdate('isActive');
  }

  highlightNavItem(path) {
    this.navItems.forEach((item) => {
      if (item.getAttribute('href') === path) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });
  }

  async checkCookieConsent() {
    const cacheKey = 'cookie_consent_cache';
    const cachedValue = localStorage.getItem(cacheKey);

    if (cachedValue !== null) {
      const hasConsent = cachedValue === 'true';

      if (hasConsent && window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted',
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted',
        });
      }

      return;
    }

    // If not in cache, sync with backend
    try {
      const response = await HTTP_CLIENT.get('/cookie-consent');
      const hasConsent = response.data.cookieConsent === true;

      if (hasConsent) {
        localStorage.setItem(cacheKey, 'true');
      }

      if (hasConsent && window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted',
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted',
        });
      }

      if (!hasConsent && this.isProductPath(this.currentPath)) return;
    } catch (error) {
      if (this.isProductPath(this.currentPath)) {
        try {
          await HTTP_CLIENT.get('/validate-auth-token');
        } catch (authError) {
          // Not authenticated, will be handled by checkAuthentication
        }
      }
    }
  }

  async checkAuthentication() {
    try {
      const response = await HTTP_CLIENT.get('/validate-auth-token');
      const hasData = !!response.data?.hasData;

      if (hasData !== this.hasData) {
        this.hasData = hasData;
        localStorage.setItem('finovva_has_data', hasData ? 'true' : 'false');
      }

      if (!hasData) {
        window.__FINOVVA_DEMO__ = true;
        if (!window.__FINOVVA_DEMO_DATA__) {
          const { getDemoData } = await import('./demo/demo-data.js');
          window.__FINOVVA_DEMO_DATA__ = getDemoData();
        }
      } else {
        window.__FINOVVA_DEMO__ = false;
        window.__FINOVVA_DEMO_DATA__ = null;
      }

      if (response.data?.fireStartTrialEvent) {
        if (typeof window.fbq !== 'undefined') {
          window.fbq('track', 'StartTrial');
        }
        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'start_trial');
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        Router.go('/landing');
      } else {
        console.error('Error validating token:', error.message);
      }
    }
  }

  async refreshStaleTickerData() {
    const storageKey = 'last_ticker_data_refresh';
    const now = Date.now();
    const TWENTY_FOUR_HOURS = 1000 * 60 * 60 * 24;

    const lastRefresh = parseInt(localStorage.getItem(storageKey), 10);
    const shouldRefresh = !lastRefresh || (now - lastRefresh > TWENTY_FOUR_HOURS);

    if (shouldRefresh) {
      try {
        await Promise.all([
          this.updateStaleProfiles(),
          this.updateStaleDividends(),
          this.updateStalePrices()
        ]);
        localStorage.setItem(storageKey, now.toString());
      } catch (error) {
        console.error('[Ticker Data Refresh] One or more updates failed:', error);
      }
    }
  }

  async updateStaleProfiles() {
    try {
      await HTTP_CLIENT.post('/update-stale-profiles');
    } catch (error) {
      console.error('[Stale Profile Update] Failed:', error);
    }
  }

  async updateStaleDividends() {
    try {
      await HTTP_CLIENT.post('/update-stale-dividends');
    } catch (error) {
      console.error('[Stale Dividend Update] Failed:', error);
    }
  }

  async updateStalePrices() {
    try {
      await HTTP_CLIENT.post('/update-stale-prices');
    } catch (error) {
      console.error('[Stale Price Update] Failed:', error);
    }
  }

  isProductPath(currentPath) {
    return currentPath.includes('/app');
  }

  get isInDemoMode() {
    return this.isProductPath(this.currentPath) && !this.hasData;
  }

  isLandingPath(currentPath) {
    return currentPath.includes('/landing');
  }

  isLoginPath(currentPath) {
    return currentPath.includes('/signup') ||
    currentPath.includes('/signin') ||
    currentPath.includes('/verification') ||
    currentPath.includes('/forgot-password') ||
    currentPath.includes('/change-password') ||
    currentPath.includes('/subscribe');
  }

  static get styles() {
    return css`
      .app-container:not(.landing-pages) {
        display: flex;
        flex-direction: row;
        min-height: 100vh;
        overflow-x: hidden;
      }

      .app-container.landing-pages {
        position: relative;
        overflow: hidden;
      }

      .nav-sidebar {
        display: inline-flex;
        position: fixed;
        flex-direction: column;
        align-items: center;
        width: 16.3%;
        height: 100%;
        padding-bottom: 0px;
        background-color: #fff;
        gap: 32px;
        flex-shrink: 0;

        @media (max-width: 1023px) {
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          width: 240px;
          z-index: 999;
          box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
          transition: 0.3s;
          transform: translateX(-130%);
        }

        .nav-active & {
          @media (max-width: 1023px) {
            transform: none;
          }
        }

        &.nav-active {
          @media (max-width: 1023px) {
            transform: none;
          }
        }
      }
      
      .nav-close {
        width: 52px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        cursor: pointer;
        color: #000;
        position: absolute;
        left: 100%;
        top: 13px;
        background: #fff;

        @media (min-width: 1024px) {
          display: none;
        }

        &:hover {
          color: rgb(51, 70, 231);
        }

        &:before,
        &:after {
          width: 20px;
          height: 2px;
          background: currentColor;
          content: '';
          display: block;
          position: relative;
        }

        &:before {
          transform: rotate(45deg);
          top: 1px;
        }

        &:after {
          transform: rotate(-45deg);
          top: -1px;
        }
      }

      .header-bar {
        position: fixed;
        flex-shrink: 0;
        /* height: 8.4%; */
        height: 80px;
        left: 16.3%;
        top: 0;
        right: 0;
        bottom: 0;
        background-color: #ffffff;
        box-sizing: border-box;
        border-bottom: 1px solid #E7E7E7;
        border-left: 1px solid #E7E7E7;
        z-index: 999;

        @media (max-width: 1199px) {
          height: 60px;
          min-height: inherit;
        }

        @media (max-width: 1023px) {
          left: 0;
        }
      }

      .logo {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        /* height: 8.45%; */
        height: 80px;
        border-bottom: 1px solid #E7E7E7;
        box-sizing: border-box;

        @media (max-width: 1199px) {
          height: 60px;
        }
      }

      .logo img {
        width: 280px;

        @media (max-width: 1479px) {
          width: 230px;
        }

        @media (max-width: 1199px) {
          width: 180px;
        }
      }

      .nav-sidebar ul {
        list-style-type: none;
        padding: 0 24px;
        max-width: 233.5px;

        @media (max-width: 1479px) {
          padding: 0 15px;
        }

        @media (max-width: 1199px) {
          padding: 0 10px;
        }

        @media (max-width: 1023px) {
          padding: 0 20px;
        }
      }

      ticker-search-sidebar-element {
        width: 100%;
      }

      .main:not(.landing-pages) {
        padding: 70px 20px 20px 20px;
        height: 100%;
      }

      .main {
        /* padding: 70px 20px 20px 20px;
        height: 100%; */

        &.main-positions {
          /* padding: 78px 0 0;
          margin-top: -24px; */
        }
      }

      .view:not(.landing-pages) {
        position: absolute;
        background-color: #f2f3f7;
        left: 16.3%;
        top: 80px;
        right: 0;
        min-height: 100vh;
        border-left: 1px solid #E7E7E7;
        overflow-x: hidden;
        /* border-top: 1px solid #E7E7E7; */

        @media (max-width: 1599px) {
          /* position: static;
          padding-left: 235px;
          padding-top: 50px;
          padding-bottom: 50px; */
        }
        
        @media (max-width: 1199px) {
          top: 60px;
        }

        @media (max-width: 1023px) {
          left: 0;

          /* padding-left: 0;
          padding-top: 30px;
          padding-bottom: 30px; */
        }
      }

      .view.has-demo-bar {
        top: 146px;
      }
      @media (max-width: 1023px) {
        .view.has-demo-bar {
          top: 108px;
        }
      }

      .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #f2f3f7;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      }

      .nav-item {
        display: inline-block;
        width: 100%;
      }

      .nav-link {
        display: flex;
        color: #535362;
        fill: #535362;
        padding: 12px 20px;
        align-items: center;
        cursor: pointer;
        text-decoration: none;

        @media (max-width: 1479px) {
          padding: 12px 15px;
        }

        @media (max-width: 1199px) {
          padding: 12px 9px;
        }

        @media (max-width: 1023px) {
          padding: 12px 20px;
        }
      }

      .nav-icon {
        display: inline-block;
        margin-right: 10px;
        width: 20px;
        height: 20px;

        @media (max-width: 1199px) {
          margin-right: 7px;
        }
      }

      .nav-text {
        font-family: "Montserrat", sans-serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: -0.16px;

        @media (max-width: 1479px) {
          font-size: 14px;
        }
      }

      .nav-link:hover {
        background-color: #f6f7f9;
        /* padding-right: 20px; */
      }

      .nav-link.active {
        color: #ffffff;
        fill: #ffffff;
        background-color: #3346e7;
        box-shadow: 0px 20px 20px 0px rgba(51, 70, 231, 0.15);
      }

      .nav-link.expanded .nav-expand {
        width: 16px;
        height: 16px;
        fill: #535362;
        transform: rotate(180deg);
      }

      .nav-expand {
        position: absolute;
        right: 10%;
        width: 16px;
        height: 16px;
        margin-right: 8px;
        fill: #535362;
        vertical-align: middle;
        transition: transform 0.2s ease-in-out;

        @media (max-width: 1199px) {
          right: 4%;
        }
      }

      .sub-nav {
        display: none;
        min-width: 195px;
        padding: 10px;

        @media (max-width: 1479px) {
          min-width: 100%;
          box-sizing: border-box;
        }
      }

      .sub-nav-link {
        display: flex;
        color: #535362;
        fill: #535362;
        padding: 12px 26px;
        align-items: flex-start;
        cursor: pointer;
        text-decoration: none;
      }

      .sub-nav-link:hover {
        background-color: #f6f7f9;
        /* padding-right: 20px; */
      }

      .sub-nav-link.active {
        color: #ffffff;
        fill: #ffffff;
        background-color: #3346e7;
        box-shadow: 0px 20px 20px 0px rgba(51, 70, 231, 0.15);
      }

      .sub-nav-icon {
        display: inline-block;
        margin-right: 10px;
        width: 24px;
        height: 24px;
      }

      .sub-nav-text {
        font-family: "Montserrat", sans-serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: -0.16px;

        @media (max-width: 1479px) {
          font-size: 14px;
        }
      }

      .sub-nav.visible {
        display: block;
      }

      .demo-bar {
        position: fixed;
        left: 16.3%;
        top: 80px;
        right: 0;
        z-index: 998;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 24px;
        background-color: #1e3a5f;
        color: #fff;
        box-sizing: border-box;
        border-left: 1px solid #E7E7E7;
      }
      @media (max-width: 1023px) {
        .demo-bar {
          left: 0;
          top: 60px;
        }
      }
      @media (max-width: 767px) {
        .demo-bar {
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }
        .demo-bar-text {
          text-align: center;
        }
        .view.has-demo-bar {
          top: 150px;
        }
      }
      @media (max-width: 600px) {
        .view.has-demo-bar {
          top: 181px;
        }
      }
      .demo-bar-text {
        margin: 0;
        font-family: "Montserrat", sans-serif;
        font-size: 14px;
        font-weight: 500;
      }
      .demo-bar-btn {
        display: inline-block;
        padding: 10px 20px;
        background-color: #fff;
        color: #1e3a5f;
        text-decoration: none;
        font-family: "Montserrat", sans-serif;
        font-size: 14px;
        font-weight: 600;
        border-radius: 4px;
        transition: opacity 0.2s;
      }
      .demo-bar-btn:hover {
        opacity: 0.9;
      }

      .landing-wrapper {
        background: #fff;
        overflow: hidden;
        width: 100%;
      }

      .landing-wrapper .main {
        padding: 0;
      }

      .landing-wrapper .main-container {
        position: static;
      }

      .header-block,
      .footer-block {
        width: 100%;
      }

      hr {
        border: none;
        height: 1px;
        background-color: #E7E7E7;
        width: calc(100% + 50px);
        margin-left: -25px;

        @media (max-width: 1024px) {
          width: calc(100% + 25px);
        }
      }
    `;
  }

  render() {
    return html`
      <whats-new-modal .currentVersion="${this.currentVersion}" .releaseDate=${this.releaseDate}></whats-new-modal>
      <cookie-consent-pill-element></cookie-consent-pill-element>
      <div class=${this.isProductPath(this.currentPath) ? 'app-container' : 'app-container landing-pages'}>
          ${this.isProductPath(this.currentPath) ? html`
            <div class="${this.isActive ? 'nav-sidebar nav-active' : 'nav-sidebar'}" id="nav-sidebar">
              <div class="nav-close" @click="${this.toggleState}"></div>
              <div class="logo">
                <img src="${logo}" alt="Logo" />
              </div>
              <ticker-search-sidebar-element @close-drawer="${this.toggleState}"></ticker-search-sidebar-element>
              <ul>
                <li class="nav-item" @click="${this.toggleState}">
                  <a class="nav-link" href="/app/dashboard">
                    <box-icon class="nav-icon" name='dashboard' type='solid'></box-icon>
                    <span class="nav-text">Dashboard</span>
                  </a>
                </li>
                <li class="nav-item">
                  <div class="nav-link expandable">
                    <box-icon class="nav-icon" name='analyse' type='solid'></box-icon>
                    <span class="nav-text">Analytics</span>
                    <box-icon class="nav-expand" name='chevron-down' type='solid'></box-icon>
                  </div>
                  <ul class="sub-nav" @click="${this.toggleState}">
                    <li class="sub-nav-item">
                      <a class="sub-nav-link" href="/app/analytics/performance">
                        <span class="sub-nav-text">Performance</span>
                      </a>
                    </li>
                    <li class="sub-nav-item">
                      <a class="sub-nav-link" href="/app/analytics/dividends">
                        <span class="sub-nav-text">Dividends</span>
                      </a>
                    </li>
                  </ul>
                </li>
                <li class="nav-item" @click="${this.toggleState}">
                  <a class="nav-link" href="/app/holdings">
                    <box-icon class="nav-icon" name='chart' type='solid'></box-icon>
                    <span class="nav-text">Holdings</span>
                  </a>
                </li>
                <li class="nav-item" @click="${this.toggleState}">
                  <a class="nav-link" href="/app/transactions">
                    <box-icon class="nav-icon" name='check-circle' type='solid'></box-icon>
                    <span class="nav-text">Transactions</span>
                  </a>
                </li>
                <li class="nav-item" @click="${this.toggleState}">
                  <a class="nav-link" href="/app/news">
                    <box-icon class="nav-icon" name='news' type='solid'></box-icon>
                    <span class="nav-text">News</span>
                  </a>
                </li>
                <li class="nav-item" @click="${this.toggleState}">
                  <a class="nav-link" href="/app/settings">
                    <box-icon class="nav-icon" type='solid' name='cog'></box-icon>
                    <span class="nav-text">Settings</span>
                  </a>
                </li>
                <hr>
                <li class="nav-item" @click="${this.toggleState}">
                  <a class="nav-link" href="https://finovva.canny.io/feedback">
                    <box-icon class="nav-icon" name='conversation' type='solid'></box-icon>
                    <span class="nav-text">Feedback</span>
                  </a>
                </li>
              </ul>
            </div>
          ` : null}

          ${this.isLandingPath(this.currentPath) && !this.isLoginPath(this.currentPath) ? html`
            <div class="header-block">
              <component-header></component-header>
            </div>
          ` : null}

          <div class=${this.isProductPath(this.currentPath) ? 'main main-positions' : 'main landing-pages'}>
            ${this.isProductPath(this.currentPath) && !this.isLoginPath(this.currentPath) ? html`
              <header-bar-element
                class="header-bar"
                .pageTitle="${this.pageTitle}"
                @toggle-click=${this.toggleState}
              ></header-bar-element>
              ${this.isInDemoMode ? html`
                <div class="demo-bar">
                  <p class="demo-bar-text">You're viewing demo data. Connect your broker to view and analyze your own investments.</p>
                  ${this.currentPath.startsWith('/app/brokerages') ? html`
                    <a class="demo-bar-btn" href="/app/dashboard">Explore with sample data</a>
                  ` : html`
                    <a class="demo-bar-btn" href="/app/brokerages">Connect your broker</a>
                  `}
                </div>
              ` : null}
            ` : null}

            <div class=${this.isProductPath(this.currentPath) ? 'view' + (this.isInDemoMode ? ' has-demo-bar' : '') : 'view landing-pages'}>
              ${this.isProductPath(this.currentPath) && !this.componentsPreloaded ? html`
                <div class="loading-overlay">
                  <spinner-element></spinner-element>
                </div>
              ` : ''}
            </div>

            ${this.currentPath !== '' && !this.isProductPath(this.currentPath) && !this.isLoginPath(this.currentPath) ? html`
              <div class="footer">
                <component-footer></component-footer>
              </div>
            ` : null}
          </div>
        </div>
      
    `;
  }
}

customElements.define('app-element', AppElement);
